import { applyHandHighlights } from '../../model/tiles.js'
import { TileRow } from '../../tiles/index.js'
import StatusBadge from '../../components/StatusBadge.jsx'
import DoraChip from '../../components/DoraChip.jsx'
import OpponentChip from '../../components/OpponentChip.jsx'
import ConceptCard from '../../components/ConceptCard.jsx'
import UkeireCard from '../../components/UkeireCard.jsx'
import FrameNav from '../../components/FrameNav.jsx'
import FrameDots from '../../components/FrameDots.jsx'

const T = {
  dark: {
    page: 'flex min-h-screen items-center justify-center bg-[#0d1117] p-6',
    root: 'flex w-[720px] min-h-[560px] flex-col gap-[14px] px-[22px] py-[18px] text-[#e7e3d8]',
    rootStyle: { background: 'radial-gradient(120% 100% at 50% 0%, #1d2733, #12181f 70%)' },
    status: 'flex items-center gap-2 rounded-lg border border-white/10 bg-white/5 px-[10px] py-[5px] text-[11px]',
    round: 'font-bold tracking-[1.5px]',
    wall: (w) => `wall ${w}`,
    wallCls: 'opacity-60',
    sticks: 'opacity-80',
    stickText: (n) => `sticks × ${n}`,
    opps: 'ml-auto flex gap-[6px]',
    middleGap: 16,
    rowGap: 14,
    drawGap: 14,
    pool: 'text-[9.5px] tracking-[1.5px] opacity-45',
    poolGap: 'gap-[10px]',
    heroW: 38,
    poolTiles: 17,
    doraW: 16,
    ukeireW: 25,
    hlc: '#e2a93b',
    label: 'WAITING ON',
    counter: (n, t) => `Frame ${n} / ${t}`,
    counterCls: 'text-[10px] opacity-50',
    keys: '← → keys',
    keysCls: 'mr-1 text-[10px] opacity-45',
    bottom: 'flex items-center gap-[10px]',
  },
  industry: {
    page: 'flex min-h-screen items-center justify-center p-6',
    root: 'flex w-[760px] min-h-[560px] flex-col gap-4 border border-neutral-300 px-[22px] py-[18px] text-[#1d1f20]',
    rootStyle: { background: 'var(--color-bg)' },
    status: 'flex items-center gap-[10px] border border-neutral-300 bg-white px-3 py-[5px] text-[11px]',
    round: 'font-heading text-[15px] font-semibold tracking-[2px]',
    wall: (w) => `Wall ${w}`,
    wallCls: 'uppercase tracking-[1px] text-neutral-600',
    sticks: 'text-neutral-700',
    stickText: (n) => `sticks × ${n}`,
    opps: 'ml-auto flex gap-[6px]',
    middleGap: 18,
    rowGap: 16,
    drawGap: 14,
    pool: 'font-heading text-[10px] tracking-[2px] text-neutral-500',
    poolGap: 'gap-3',
    heroW: 38,
    poolTiles: 17,
    doraW: 16,
    ukeireW: 25,
    hlc: 'var(--color-accent)',
    label: 'WAITING ON',
    counter: (n, t) => `FRAME ${n} / ${t}`,
    counterCls: 'text-[10.5px] tracking-[1px] text-neutral-600',
    keys: '← → KEYS',
    keysCls: 'mr-1 text-[10px] tracking-[1px] text-neutral-500',
    bottom: 'flex items-center gap-3 border-t border-neutral-300 pt-3',
  },
}

export default function FocusLayout({ theme, frame, index, total, next, prev, goTo }) {
  const m = T[theme]

  const doraTiles = [{ code: frame.doraInd }]
  const heroTiles = applyHandHighlights(frame.hero.hand, frame.hero.hl)
  const heroDraw = frame.hero.draw
    ? [{ code: frame.hero.draw, highlight: !!frame.hero.drawHl }]
    : []

  const opps = ['left', 'top', 'right'].map((k) => {
    const p = frame[k]
    const last = p.discards.length ? [{ code: p.discards[p.discards.length - 1].code }] : []
    return { wind: p.wind, name: p.name, score: p.score, riichi: p.riichi, last }
  })

  return (
    <div className={m.page}>
      <div className="select-none" style={{ outline: 'none', userSelect: 'none' }}>
        <div className={m.root} style={m.rootStyle}>
          <div className="flex flex-wrap items-center gap-2">
            <div className={m.status}>
              <span className={m.round}>{frame.round}</span>
              <span className={m.wallCls}>{m.wall(frame.wall)}</span>
              <DoraChip tiles={doraTiles} width={m.doraW} theme={theme} />
              {frame.sticks > 0 && <span className={m.sticks}>{m.stickText(frame.sticks)}</span>}
            </div>
            <div className={m.opps}>
              {opps.map((o) => (
                <OpponentChip key={o.wind} theme={theme} {...o} />
              ))}
            </div>
          </div>

          <div className="flex flex-1 flex-col justify-center" style={{ gap: m.middleGap }}>
            <div className="flex items-stretch" style={{ gap: m.rowGap }}>
              <ConceptCard frameNo={index + 1} title={frame.title} body={frame.body} theme={theme} />
              {frame.ukeire && (
                <UkeireCard
                  theme={theme}
                  label={m.label}
                  tiles={frame.ukeire.tiles}
                  note={frame.ukeire.note}
                  width={m.ukeireW}
                  vertical
                  className="flex-none"
                />
              )}
            </div>
            <div className="flex items-end justify-center" style={{ gap: 2 }}>
              <TileRow tiles={heroTiles} width={m.heroW} gap={2} highlightColor={m.hlc} />
              <div style={{ width: m.drawGap }} />
              <TileRow tiles={heroDraw} width={m.heroW} highlightColor={m.hlc} />
            </div>
            <div className={`flex items-center justify-center ${m.poolGap}`}>
              <span className={m.pool}>YOUR POND</span>
              <TileRow tiles={frame.hero.discards} width={m.poolTiles} gap={2} highlightColor={m.hlc} />
              {frame.hero.riichi && <StatusBadge kind="riichi" theme={theme} />}
              {frame.win && <StatusBadge kind="tsumo" theme={theme} />}
            </div>
          </div>

          <div className={m.bottom}>
            <FrameDots total={total} index={index} goTo={goTo} theme={theme} />
            <span className={m.counterCls}>{m.counter(index + 1, total)}</span>
            <div className="ml-auto flex items-center gap-[6px]">
              <span className={m.keysCls}>{m.keys}</span>
              <FrameNav theme={theme} prev={prev} next={next} frameNo={index + 1} total={total} showCounter={false} />
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
